import * as THEOS from "../interfaces/theos.interface";
import { IGameSettings } from "./types";
import { IUIActionDetails } from "../interfaces/feim.interface";
import { isHandshakeDone } from "./configuration";
import { registerEventHandler } from "./registerEventHandler";

/**
 * Triggered once the environment configuration is received from both the game and the operator.
 * Handshake is done and everything is set up.
 * 
 * Can be useful if you need to do something with [[getConfiguration]] early on, as the values will be undefined before this.
 * Otherwise there's no need to listen to this.
 * @param callback 
 */
export const initialized = (callback: () => void): void => {
    if (isHandshakeDone()) {
        callback();
    } else {
        registerEventHandler(THEOS.OperatorEvent._INITIALIZED)(callback);
    }
};

/**
 * The operator requests a balance update.
 *
 * Request Casino World API `/getbalance/` and then use send.[[balanceUpdate]] to send back the refreshed balance.
 */
export const refreshBalance = (callback: () => void): void => {
    registerEventHandler(THEOS.OperatorEvent.REFRESH_BALANCE)(callback);
};

/**
 * Settings have been changed in the operator front end, they need to be synchronized in game.
 *
 * The settings argument only contains the changed settings, unchanged settings are undefined:
 * ```
 * on.updateSettings((changedSettings) =>
 * {
 *   if (changedSettings.sounds !== undefined) {
 *     enableSounds(changedSettings.sounds)
 *   }
 *
 *   if (changedSettings.fastPlay !== undefined) {
 *     enableFastPlay(changedSettings.fastPlay)
 *   }
 * })
 * ```
 *
 * @param callback A function to update in-game settings to match
 */
export const updateSettings = (callback: (settings: IGameSettings) => void): void =>
{
    // Not just returning this because tsdoc will not document the type
    registerEventHandler<IGameSettings>(THEOS.OperatorEvent.UPDATE_SETTINGS)(callback);
};

/**
 * Paytable should be shown, hidden, or its visibility toggled (if applicable).
 *
 * If the action argument is undefined or doesn't specify the action type, toggle paytable visibility.
 * 
 * If `action?.type` is for example `"show"` but the paytable is already visible, this event should do nothing.
 */
export const togglePaytable = (callback: (details?: IUIActionDetails) => void): void =>
{
    registerEventHandler<IUIActionDetails>(THEOS.OperatorEvent.PAYTABLE_TOGGLE)(callback);
};

/**
 * If you do not want to use [[showError]], this can be used to detect when an error message
 * popup is dismissed in the operator window.
 */
export const errorMessageDismissed = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.ERROR_MESSAGE_DISMISSED)(callback);
};

/**
 * An error message was displayed in the operator window.
 */
export const errorMessageDisplayed = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.ERROR_MESSAGE_DISPLAYED)(callback);
};

/**
 * Game help should be shown, hidden, or its visibility toggled.
 * 
 * If the action argument is undefined or doesn't specify the action type, toggle game help visibility.
 * 
 * If `action?.type` is for example `"show"` but game help is already visible, this event should do nothing.
 */
export const toggleGameHelp = (callback: (action?: IUIActionDetails) => void): void =>
{
    registerEventHandler<IUIActionDetails>(THEOS.OperatorEvent.TOGGLE_GAME_HELP)(callback);
};

/**
 * The game will be closed imminently.
 *
 * The operator will handle closing the game frame; this function can be used to for example send any last second messages.
 */
export const exitingGame = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.EXITING_GAME)(callback);
};

/**
 * Pause autoplay, or stop it if pausing is not possible.
 */
export const pauseAutoPlay = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.PAUSE_AUTOPLAY)(callback);
};

/**
 * @hidden
 * Disable play controls so that no further gameplay is possible until on.[[unlockPlay]] is received.
 *
 * Autoplay should be paused, if active.
 */
export const lockPlay = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.LOCK_PLAY)(callback);
};

/**
 * @hidden
 * Enable play controls if they were disabled by on.[[lockPlay]]
 */
export const unlockPlay = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.UNLOCK_PLAY)(callback);
};

/**
 * When received, pause the game, prevent further game play, and block user interaction. Unfreeze when on.[[unfreeze]] is received.
 *
 * If received during an active game round, the game animation should be paused immediately instead of waiting until the end of the round,
 * as sometimes the game must be paused mid-round before a feature/bonus game starts. Reality check related freezes are automatically delayed
 * until any ongoing game round has ended.
 * 
 * Any ongoing autoplay must be paused.
 */
export const freeze = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.FREEZE)(callback);
};

/**
 * When received, unfreeze the game if it's been frozen by on.[[freeze]]
 */
export const unfreeze = (callback: () => void): void =>
{
    registerEventHandler(THEOS.OperatorEvent.UNFREEZE)(callback);
};